import { gql } from '@apollo/client'
import {
  CONTENT_SPOTLIGHT_SSR_INLINE,
  CONTENT_TILE_INLINE_FULL,
} from '../content/fragments'

/** Client bootstrap — inline tabContent on all row types. */
export const GET_PM_SCREEN_PERSONALIZED = gql`
  ${CONTENT_TILE_INLINE_FULL}
  query GetPmScreenPersonalized($screenType: String!, $language: [String], $sectionsLimit: Int, $contentLanguage: [String], $imageType: [String], $learnerLevelLanguage: String!) {
    pmScreenPersonalized(screenType: $screenType, language: $language) {
      pmScreen {
        id
        screenType
        language
        pmSections(limit: 300, order: [["order", "ASC"]]) {
          id
        }
        pmSectionsWithDetails: pmSections(limit: $sectionsLimit, order: [["order", "ASC"]]) {
          id
          type
          adminTitle
          visibility {
            type
            inverse
            content {
              contentType
              contentId
            }
            subscriptions
            threshold
          }
          app {
            id
            type
            adminTitle
            enabled
            visibility {
              type
              inverse
              content {
                contentType
                contentId
              }
              subscriptions
              threshold
            }
            data {
              ... on PMSectionAppDataContentList {
                contextType
                displayMode
                enableContentPreferencesFiltering
                enableUserHiddenContent
                logSectionVisibility
                hideWatched
                sortOrder
                recommId
                footer {
                  label
                }
                tabs {
                  tabLabel
                  merchEventId
                  viewMode
                  slug
                  payloadType
                  pineScore
                  pineSource
                  tabContent {
                    contentId
                    contentType
                    label
                    verticalThumbnailUrl
                    seedQuestion {
                      id
                      question
                      language
                      tags
                    }
                    pineSource
                    pineScore
                    videoTasteSegment
                    content(language: $contentLanguage, limit: 1, offset: 0) {
                      ...ContentTileInlineFull
                    }
                  }
                }
                header {
                  label
                }
                render {
                  type
                  background
                  tileCount
                  link
                }
                content {
                  listId
                  label
                }
              }
              ... on PMSectionAppDataContinueWatching {
                title
                description
                header {
                  label
                }
                tabs {
                  tabLabel
                  merchEventId
                  viewMode
                  slug
                  payloadType
                  pineScore
                  pineSource
                  tabContent {
                    contentId
                    contentType
                    label
                    seedQuestion {
                      id
                      question
                      language
                      tags
                    }
                    pineSource
                    pineScore
                    videoTasteSegment
                    content(language: $contentLanguage, limit: 1, offset: 0) {
                      ...ContentTileInlineFull
                    }
                  }
                }
                render {
                  type
                  background
                  tileCount
                  link
                }
              }
              ... on PMSectionAppDataPlacement {
                placement
                recommId
                contextType
                tabs {
                  tabLabel
                  merchEventId
                  viewMode
                  slug
                  payloadType
                  pineScore
                  pineSource
                  tabContent {
                    contentId
                    contentType
                    label
                    seedQuestion {
                      id
                      question
                      language
                      tags
                    }
                    pineSource
                    pineScore
                    videoTasteSegment
                    title
                    subTitle
                    description
                    mobileBackgroundUrl
                    countdown
                    eventType
                    webviewUrl
                    content(language: $contentLanguage, limit: 1, offset: 0) {
                      ...ContentTileInlineFull
                    }
                  }
                }
                render {
                  type
                  background
                  tileCount
                  link
                }
              }
              ... on PMSectionAppDataHeaderSection {
                imageId
                imageUrl
                title
                description
                asset {
                  id
                  renderUrl
                }
              }
              ... on PMSectionAppDataMemberHomeFirstTouch {
                contextType
                displayMode
                title
                description
                recommId
                tabs {
                  tabType
                  tabLabel
                  tabContent {
                    contentId
                    contentType
                    label
                    content(language: $contentLanguage, limit: 1, offset: 0) {
                      ...ContentTileInlineFull
                    }
                  }
                }
              }
            }
            provider
            providerKey
            clientAttributes
            availabilityClientAttributes
            createdAt
            updatedAt
            order
            pmScreenId
          }
          pmList(limit: 100) {
            id
            type
            language
            adminTitle
            title
            description
            listItems {
              type
              id
              imageId
              imageUrl
              title
              childListId
            }
            createdAt
            updatedAt
          }
        }
      }
      screenType
    }
  }
`

/**
 * Member-home SSR — inline Content only on spotlight placement rows; tile rows
 * resolve via client GetPmSectionList / GET_CONTENT after first paint.
 */
export const GET_PM_SCREEN_PERSONALIZED_SSR = gql`
  ${CONTENT_SPOTLIGHT_SSR_INLINE}
  query GetPmScreenPersonalizedSsr($screenType: String!, $language: [String], $sectionsLimit: Int, $contentLanguage: [String], $imageType: [String]) {
    pmScreenPersonalized(screenType: $screenType, language: $language) {
      pmScreen {
        id
        screenType
        language
        pmSections(limit: 300, order: [["order", "ASC"]]) {
          id
        }
        pmSectionsWithDetails: pmSections(limit: $sectionsLimit, order: [["order", "ASC"]]) {
          id
          type
          adminTitle
          visibility {
            type
            inverse
            content {
              contentType
              contentId
            }
            subscriptions
            threshold
          }
          app {
            id
            type
            adminTitle
            enabled
            visibility {
              type
              inverse
              content {
                contentType
                contentId
              }
              subscriptions
              threshold
            }
            data {
              ... on PMSectionAppDataContentList {
                contextType
                displayMode
                enableContentPreferencesFiltering
                enableUserHiddenContent
                logSectionVisibility
                hideWatched
                sortOrder
                recommId
                footer {
                  label
                }
                tabs {
                  tabLabel
                  merchEventId
                  viewMode
                  slug
                  payloadType
                  pineScore
                  pineSource
                  tabContent {
                    contentId
                    contentType
                    label
                    verticalThumbnailUrl
                    seedQuestion {
                      id
                      question
                      language
                      tags
                    }
                    pineSource
                    pineScore
                    videoTasteSegment
                  }
                }
                header {
                  label
                }
                render {
                  type
                  background
                  tileCount
                  link
                }
                content {
                  listId
                  label
                }
              }
              ... on PMSectionAppDataContinueWatching {
                title
                description
                header {
                  label
                }
                tabs {
                  tabLabel
                  merchEventId
                  viewMode
                  slug
                  payloadType
                  pineScore
                  pineSource
                  tabContent {
                    contentId
                    contentType
                    label
                    seedQuestion {
                      id
                      question
                      language
                      tags
                    }
                    pineSource
                    pineScore
                    videoTasteSegment
                  }
                }
                render {
                  type
                  background
                  tileCount
                  link
                }
              }
              ... on PMSectionAppDataPlacement {
                placement
                recommId
                contextType
                tabs {
                  tabLabel
                  merchEventId
                  viewMode
                  slug
                  payloadType
                  pineScore
                  pineSource
                  tabContent {
                    contentId
                    contentType
                    label
                    seedQuestion {
                      id
                      question
                      language
                      tags
                    }
                    pineSource
                    pineScore
                    videoTasteSegment
                    title
                    subTitle
                    description
                    mobileBackgroundUrl
                    countdown
                    eventType
                    webviewUrl
                    content(language: $contentLanguage, limit: 1, offset: 0) {
                      ...ContentSpotlightSsrInline
                    }
                  }
                }
                render {
                  type
                  background
                  tileCount
                  link
                }
              }
              ... on PMSectionAppDataHeaderSection {
                imageId
                imageUrl
                title
                description
                asset {
                  id
                  renderUrl
                }
              }
              ... on PMSectionAppDataMemberHomeFirstTouch {
                contextType
                displayMode
                title
                description
                recommId
                tabs {
                  tabType
                  tabLabel
                  tabContent {
                    contentId
                    contentType
                    label
                  }
                }
              }
            }
            provider
            providerKey
            clientAttributes
            availabilityClientAttributes
            createdAt
            updatedAt
            order
            pmScreenId
          }
          pmList(limit: 100) {
            id
            type
            language
            adminTitle
            title
            description
            listItems {
              type
              id
              imageId
              imageUrl
              title
              childListId
            }
            createdAt
            updatedAt
          }
        }
      }
      screenType
    }
  }
`

export default GET_PM_SCREEN_PERSONALIZED
